import { isBrowser } from "@/utils/env";

import { MARKETING_YANDEX_ID } from "./constants";

const METRIKA_SCRIPT_SRC = "https://mc.yandex.ru/metrika/tag.js";
const METRIKA_COOKIE_PREFIX = "_ym_";
const METRIKA_COOKIE_NAMES = ["yandexuid", "ymex", "yuidss", "usst", "i"];
const GET_CLIENT_ID_TIMEOUT_MS = 3000;
const YM_READY_TIMEOUT_MS = 3000;
const YM_POLL_INTERVAL_MS = 50;

let cachedClientID = "";
let pendingClientIDPromise: Promise<string> | null = null;
let isYandexMetrikaInitialized = false;

/**
 * Создаёт очередь команд `window.ym` и ставит в неё `init` счётчика.
 *
 * Вызывать до вставки `tag.js`: скрипт забирает команды из очереди только в момент своей загрузки,
 * а очередь, созданную позже, не подхватывает — тогда счётчик не стартует и `getClientID` не отвечает.
 * Повторный вызов ничего не делает.
 */
export const initYandexMetrika = (): void => {
  if (!isBrowser() || !MARKETING_YANDEX_ID || isYandexMetrikaInitialized) return;

  isYandexMetrikaInitialized = true;

  if (typeof window.ym === "undefined") {
    window.ym = function ymQueue() {
      // Метрика ждёт в очереди именно объекты `arguments`, как в её официальном сниппете.
      // eslint-disable-next-line prefer-rest-params
      (window.ym.a = window.ym.a || []).push(arguments);
    };
    window.ym.l = Date.now();
  }

  window.ym(MARKETING_YANDEX_ID, "init", {
    ssr: true,
    clickmap: true,
    trackLinks: true,
    accurateTrackBounce: true,
    webvisor: true,
    triggerEvent: true,
    ecommerce: "dataLayer",
    referrer: document.referrer,
    url: window.location.href,
  });
};

/**
 * Ждёт появления `window.ym` и сообщает, доступна ли Метрика.
 *
 * `window.ym` создаётся в `initYandexMetrika` из эффекта `YandexMetrikaHelmet`, то есть уже после
 * первого рендера. Поэтому если `window.ym` ещё нет, мы не фиксируем недоступность сразу,
 * а поллим его появление до конца таймаута.
 *
 * Возвращает `false`, если Метрика так и не появилась (таймаут, отключена, вырезана adblock'ом)
 * или если кода вообще нет в браузере (SSR-сборка Gatsby).
 */
export const whenYandexMetrikaReady = (
  timeoutMs: number = YM_READY_TIMEOUT_MS,
): Promise<boolean> =>
  new Promise((resolve) => {
    if (typeof window === "undefined" || !MARKETING_YANDEX_ID) {
      resolve(false);
      return;
    }

    if (typeof window.ym !== "undefined") {
      resolve(true);
      return;
    }

    let pollId: number | undefined;

    const timeoutId = window.setTimeout(() => {
      if (pollId !== undefined) window.clearInterval(pollId);
      resolve(false);
    }, timeoutMs);

    pollId = window.setInterval(() => {
      if (typeof window.ym === "undefined") return;
      window.clearInterval(pollId!);
      window.clearTimeout(timeoutId);
      pollId = undefined;
      resolve(true);
    }, YM_POLL_INTERVAL_MS);
  });

/**
 * Возвращает ClientID Яндекс.Метрики через официальный JS-метод `ym('getClientID', cb)`
 * и кэширует его в памяти модуля.
 *
 * Ждёт не дольше GET_CLIENT_ID_TIMEOUT_MS (общий таймаут на появление `window.ym` и ответ
 * `getClientID`) и тогда возвращает пустую строку. Пустое значение не кэшируется: если Метрика
 * ответит позже, ClientID всё равно сохранится, а следующий вызов спросит Метрику заново.
 * На стороне бэка пустой CID приводит к skip отправки хита.
 */
export const getMetricaCID = (): Promise<string> => {
  if (cachedClientID) return Promise.resolve(cachedClientID);
  if (pendingClientIDPromise) return pendingClientIDPromise;
  if (!isBrowser() || !MARKETING_YANDEX_ID) return Promise.resolve("");

  const request = new Promise<string>((resolve) => {
    const timeoutId = window.setTimeout(() => {
      resolve("");
    }, GET_CLIENT_ID_TIMEOUT_MS);

    whenYandexMetrikaReady(GET_CLIENT_ID_TIMEOUT_MS).then((isMetrikaReady) => {
      if (!isMetrikaReady) {
        window.clearTimeout(timeoutId);
        resolve("");
        return;
      }

      window.ym(MARKETING_YANDEX_ID, "getClientID", (cid: string) => {
        window.clearTimeout(timeoutId);
        if (cid) cachedClientID = cid;
        resolve(cachedClientID);
      });
    });
  });

  pendingClientIDPromise = request;
  request.then(() => {
    if (pendingClientIDPromise === request) pendingClientIDPromise = null;
  });

  return request;
};

/**
 * Синхронно возвращает закэшированный ClientID. Если ещё не получен — пустая строка.
 * Использовать в Apollo-link middleware, где callback ждать нельзя.
 */
export const getCachedMetricaCID = (): string =>
  cachedClientID;

const getDomainVariants = (): string[] => {
  const { hostname } = window.location;
  if (!hostname || hostname === "localhost") {
    return [hostname];
  }

  const parts = hostname.split(".");
  const domains = new Set<string>([hostname]);

  for (let index = 0; index < parts.length - 1; index += 1) {
    const domain = parts.slice(index).join(".");
    domains.add(domain);
    domains.add(`.${domain}`);
  }

  return Array.from(domains);
};

const expireCookie = (name: string, path: string, domain?: string): void => {
  const domainPart = domain ? `; domain=${domain}` : "";
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}${domainPart}`;
};

const removeCookieEverywhere = (name: string): void => {
  const paths = ["/", window.location.pathname];
  const domains = [...getDomainVariants(), undefined];

  paths.forEach((path) => {
    domains.forEach((domain) => {
      expireCookie(name, path, domain);
    });
  });
};

const clearCookies = (): void => {
  const names = document.cookie
    .split(";")
    .map((pair) =>
      pair.split("=")[0]?.trim())
    .filter(Boolean) as string[];

  names.forEach((name) => {
    if (
      name.startsWith(METRIKA_COOKIE_PREFIX)
      || METRIKA_COOKIE_NAMES.includes(name)
    ) {
      removeCookieEverywhere(name);
    }
  });
};

const clearStorage = (): void => {
  const storagePrefixes = ["_ym", `metrika_enabled_${MARKETING_YANDEX_ID}`];

  [window.localStorage, window.sessionStorage].forEach((storage) => {
    const keysToRemove: string[] = [];

    for (let index = 0; index < storage.length; index += 1) {
      const key = storage.key(index);
      if (!key) {
        continue;
      }
      if (storagePrefixes.some((prefix) =>
        key.startsWith(prefix))) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((key) =>
      storage.removeItem(key));
  });
};

const removeMetrikaScript = (): void => {
  // Поиск по началу адреса: в разметку тег попадает с номером счётчика в параметре
  // (`tag.js?id=…`), и точное совпадение с базовым адресом его не находит.
  const scripts = Array.from(document.querySelectorAll(`script[src^="${METRIKA_SCRIPT_SRC}"]`));
  scripts.forEach((script) =>
    script.remove());
};

export const disableYandexMetrikaAndClearData = (): void => {
  cachedClientID = "";
  pendingClientIDPromise = null;
  isYandexMetrikaInitialized = false;
  removeMetrikaScript();
  clearStorage();
  clearCookies();

  delete (window as Window & { ym?: unknown }).ym;
  delete (window as Window & { [key: string]: unknown })[`yaCounter${MARKETING_YANDEX_ID}`];
};
